import { Component, OnInit, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { TranslationService } from './services/translation.service';
import { AuthStateService } from './services/auth-state.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'COOPROGETTI Referenze';

  private readonly authState = inject(AuthStateService);
  private readonly logger = inject(NGXLogger);
  private readonly translationService = inject(TranslationService);
  private readonly router = inject(Router);

  /** Stato di autenticazione reattivo. Nel template: `isAuthenticated()`. */
  readonly isAuthenticated = this.authState.isAuthenticated;

  /** True quando si è sulla route principale, anche con query params. Nel template: `showHome()`. */
  readonly showHome = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.isHomePathname(event.urlAfterRedirects || event.url)),
    ),
    { initialValue: this.isHomePathname(this.router.url) },
  );

  // Bridge signal -> Observable creato in un injection context valido (field initializer).
  // NON spostare dentro un metodo/ngOnInit: toObservable() lancerebbe NG0203.
  private readonly isAuthenticated$ = toObservable(this.authState.isAuthenticated);

  ngOnInit(): void {
    // Il salvataggio dei dati utente è gestito dal CallbackComponent dopo il login;
    // qui ci occupiamo solo dell'inizializzazione della app shell.
    this.translationService.setItalianTranslations();
    this.savePendingRedirectUrlIfNeeded();
  }

  login(): void {
    this.logger.info('[APP.COMPONENTE] - login');
    this.authState.login();
  }

  logout(): void {
    this.clearSession();
    this.logger.info('[APP.COMPONENTE] - logout');
    this.authState.logout();
  }

  /**
   * Se l'utente non è autenticato e l'URL corrente ha query params, li salva
   * per poterli ripristinare dopo il login.
   */
  private savePendingRedirectUrlIfNeeded(): void {
    this.isAuthenticated$
      .pipe(
        // Il signal parte da `null` (stato non ancora determinato): aspettiamo il primo valore definitivo.
        filter((isAuthenticated): isAuthenticated is boolean => isAuthenticated !== null),
        take(1), // si auto-completa: nessuna gestione manuale della subscription necessaria
      )
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          return;
        }

        const currentUrl = this.router.url;
        if (currentUrl.includes('?')) {
          sessionStorage.setItem('pendingRedirectUrl', currentUrl);
          this.logger.debug('[APP.COMPONENTE] - URL salvato per redirect dopo login:', currentUrl);
        }
      });
  }

  private isHomePathname(url: string): boolean {
    const pathname = url.split('?')[0];
    return pathname === '/' || pathname === '';
  }

  private clearSession(): void {
    this.logger.info('[APP.COMPONENTE] - clearSession');

    sessionStorage.clear();
    localStorage.clear();

    this.logger.info('[APP.COMPONENTE] - Storage locale e di sessione puliti completamente');
  }
}